import { Component, OnInit, Input, OnDestroy, ViewChild, ChangeDetectorRef, OnChanges } from '@angular/core';
import { MeteringPoint } from '@model';
import * as mpStore from '@appStore/metering-points';
import { Store } from '@ngrx/store';
import { String } from 'typescript-string-operations';
import { Observable } from 'rxjs';
import { MeteringPointFunctionControl } from 'app/models/metering-point-function-control';
import { filter, take } from 'rxjs/operators';
import { environment } from '@environment';
import { SubSink } from 'subsink';
import { SxSnackbarService } from '@services';
import { TranslateService } from '@ngx-translate/core';
import { MeteringPointService } from '@appStore/metering-points/services';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { FunctionControlListingTableDataSource } from './function-control-listing-table-datasource';
import { ToLocalDateTimePipe } from '@utils/pipes';

@Component({
    selector: 'app-function-control-listing',
    templateUrl: './function-control-listing.component.html',
    standalone: false
})
export class FunctionControlListingComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  meteringPoint: MeteringPoint;
  @Input()
  containedMeteringPoint: MeteringPoint;

  @ViewChild(MatPaginator, { static: true })
  paginator: MatPaginator;

  @ViewChild(MatSort, { static: true })
  sort: MatSort;

  meteringPointsWithFunctionsControls$: Observable<MeteringPoint[]>;
  functionControls: MeteringPointFunctionControl[];
  dataSource: FunctionControlListingTableDataSource;
  selectedMeteringPoint: MeteringPoint;
  isContainerMeteringPoint = false;

  sortActive: string;
  sortDirection: string;
  pageSize: number;
  currentPage = 0;

  displayedColumns = [
    'id',
    'performed',
    'notes'
  ];

  private subs = new SubSink();

  constructor(
    private meteringStore: Store<mpStore.State>,
    private sxSnackbarService: SxSnackbarService,
    private translate: TranslateService,
    private meteringPointService: MeteringPointService,
    private toLocalDate: ToLocalDateTimePipe,
    private changeDetectorRef: ChangeDetectorRef
  ) {
        this.sortActive = 'performed';
        this.sortDirection = 'desc';
  }

  ngOnInit() {
   
    if (
      this.meteringPoint.type !== 'LOG' &&
      this.meteringPoint.hasFunctionControl && 
      !this.meteringPoint.functionControls &&
      !this.meteringPoint.functionControlsLoaded &&
      !this.meteringPoint.functionControlsLoading
    ) {
      this.selectedMeteringPoint = this.meteringPoint;

      this.meteringStore.dispatch(
        new mpStore.LoadMeteringPointFunctionControls({
          masterMeteringPointId: this.meteringPoint.id,
          meteringPointId: this.meteringPoint.id,
      }));
    }

  if (this.meteringPoint.type === 'LOG' && this.containedMeteringPoint?.hasFunctionControl) {

        if (this.containedMeteringPoint.hasFunctionControl && 
            !this.containedMeteringPoint.functionControlsLoaded && 
            !this.containedMeteringPoint.functionControlsLoading) {

          this.selectedMeteringPoint = this.containedMeteringPoint;
          this.isContainerMeteringPoint = true;

           this.meteringStore.dispatch(
              new mpStore.LoadMeteringPointFunctionControls({
                masterMeteringPointId: this.meteringPoint.id,
                meteringPointId: this.containedMeteringPoint.id
              })
            );
        } 
  }

    this.meteringPointsWithFunctionsControls$ = this.meteringStore.select(mpStore.getMeteringPointFunctionControls);
    this.subs.sink = this.meteringPointsWithFunctionsControls$.pipe(filter(x => !!x)).subscribe(mps => {
  
      // Master meteringPoint
      let filteredMp = mps.find(mp => mp.id === this.meteringPoint.id);

      if(filteredMp) {
        if(!this.isContainerMeteringPoint) {
          this.functionControls = filteredMp ? filteredMp.functionControls : [];
        } else {
          // Contained meteringPoint
          let filteredContainedMp = filteredMp.containedMeteringPoints.find(mp => mp.id === this.containedMeteringPoint.id);
          this.functionControls = filteredContainedMp ? filteredContainedMp.functionControls : [];
        }
        this.createLocalView();
      }
    });
  }

  ngOnChanges(): void {

    if (this.containedMeteringPoint && this.containedMeteringPoint.hasFunctionControl && this.containedMeteringPoint.functionControlsLoaded) {
      // Set already loaded Function-controls
      this.functionControls = this.containedMeteringPoint ? this.containedMeteringPoint.functionControls : [];
    }

    this.createLocalView();

    // Force the view to update
    this.changeDetectorRef.detectChanges();
  }
  
  createLocalView() {

    if (!this.functionControls || this.functionControls.length === 0) {
      this.paginator.pageSize = this.pageSize;
      this.dataSource = new FunctionControlListingTableDataSource(
        this.paginator,
        this.sort,
        []
      );
      return;
    }

    this.paginator.pageSize = this.pageSize;
    this.dataSource = new FunctionControlListingTableDataSource(this.paginator, this.sort, this.functionControls);
  }

  onPagination(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
  }


  getReportKey(functionControlId: number) {
    let ts = '' + new Date().getTime();
    let win = window.open('about:blank', ts);
    win.location.replace('/report-download.html?ts=' + ts);
    win['selfRef'] = win;

    let filename = 'Funktionskontroll-' + functionControlId + '-' + this.meteringPoint.id + '-' + this.replaceInvalidChars(this.meteringPoint.name);
    let timestamp = this.toLocalDate.transform(new Date(), 'YYYY-MM-dd');

    this.sxSnackbarService.openSnackBar(this.translate.instant('meteringpoints.info.downloading'), '');

    this.subs.sink = this.meteringPointService
      .createReportKey()
      .pipe(
        filter(x => !!x),
        take(1)
      )
      .subscribe(
        key => {
          let reportUrl = String.Format(
            '{0}/Reports/{1}/meteringpoints/{2}/functioncontrol/{3}/{4}_{5}',
            environment.apiEndpoint,
            key,
            this.meteringPoint.id,
            functionControlId,
            filename,
            timestamp
          );
          win.location.replace('/report-download.html?ts=' + ts + '&redir=' + encodeURIComponent(reportUrl));
          win['selfRef'] = win;
          win.focus();
        },
        () => {
          let errorUrl = String.Format('{0}/error', window.location.origin);
          win.location.href = errorUrl;
          win.focus();
        }
      );
  }

  replaceInvalidChars = s => {
    return s.replace(/[/\\?%*:|"<>+]/g, '-');
  };


  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
